/*
  File: src/contexts/StreamingContext.js
  Purpose: Central streaming state machine and actions for live video playback.
  Responsibilities:
  - Wrap `StreamingService` to start/stop streams and poll for status.
  - Detect and connect to existing live streams and fetch playback URL.
  - Expose state (streamingState, isLoading, playbackUrl, error, streamStatus) and
    actions (startStream, stopStream, checkExistingStream, clearError).
  States:
  - idle → starting → live → idle/error
  Notes:
  - `baseUrl` is provided by the top-level provider and passed to the service.
  - Consumer hooks must be used inside `StreamingProvider`.
*/
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import StreamingService from '../services/StreamingService';
const DEBUG_STREAM = String(process.env.REACT_APP_DEBUG_STREAM || '').toLowerCase() === 'true';

// Simple streaming states
export const STREAMING_STATES = {
    IDLE: 'idle',
    STARTING: 'starting',
    LIVE: 'live',
    STOPPING: 'stopping',
    ERROR: 'error'
};

// Context
const StreamingContext = createContext();

// Provider component
export function StreamingProvider({ children, baseUrl, defaultType = 'match' }) {
    const [streamingService] = useState(() => new StreamingService(baseUrl));

    // Core state
    const [streamingState, setStreamingState] = useState(STREAMING_STATES.IDLE);
    const [isLoading, setIsLoading] = useState(false);
    const [playbackUrl, setPlaybackUrl] = useState('');
    const [error, setError] = useState(null);
    const [streamStatus, setStreamStatus] = useState(null);
    const [streamSessionId, setStreamSessionId] = useState('');
    const [streamType, setStreamType] = useState(defaultType);
    const waitAbortRef = React.useRef(false);
    const explicitlyStoppedRef = React.useRef(false);

    // Clear error helper
    const clearError = useCallback(() => {
        setError(null);
    }, []);

    // Clear stream state helper
    const clearStreamState = useCallback(() => {
        setPlaybackUrl('');
        setStreamStatus(null);
        setStreamSessionId('');
        setStreamingState(STREAMING_STATES.IDLE);
        setError(null);
        setIsLoading(false);
        if (DEBUG_STREAM) console.log('Stream state cleared');
    }, []);

    // Check if stream is already running
    const checkExistingStream = useCallback(async (typeToCheck = null) => {
        // Don't auto-connect if user explicitly stopped the stream
        if (explicitlyStoppedRef.current) {
            if (DEBUG_STREAM) console.log('Skipping auto-connect - stream was explicitly stopped');
            return false;
        }

        const checkType = typeToCheck || streamType || 'match';
        try {
            const status = await streamingService.getStatus(checkType);
            const statusStr = String(status?.status || '').toLowerCase();
            const isIvsLive = status?.ivs_state === 'LIVE';
            const isLocalLive = status?.running && (statusStr === 'live' || statusStr === 'streaming');
            if (isIvsLive || isLocalLive) {
                if (DEBUG_STREAM) console.log('Found existing live stream:', status);
                setStreamStatus(status);
                setStreamingState(STREAMING_STATES.LIVE);
                const prevSid = streamSessionId;
                if (status?.stream_session_id) setStreamSessionId(status.stream_session_id);

                // Get playback URL for existing stream
                try {
                    const playbackData = await streamingService.getPlaybackUrl(checkType);
                    let url = playbackData.playback_url;
                    // If the stream session changed but the playback URL is identical (common with IVS),
                    // bust the cache so the player fully reloads to the new live session.
                    if (status?.stream_session_id && status.stream_session_id !== prevSid) {
                        const sep = url.includes('?') ? '&' : '?';
                        url = `${url}${sep}sid=${encodeURIComponent(status.stream_session_id)}&t=${Date.now()}`;
                        if (DEBUG_STREAM) console.log('Session changed, forcing player reload with cache-busted URL');
                        // Force a full teardown/re-init by clearing the URL first
                        setPlaybackUrl('');
                        setTimeout(() => setPlaybackUrl(url), 0);
                    } else {
                        setPlaybackUrl(url);
                    }
                } catch (e) {
                    if (DEBUG_STREAM) console.warn('Failed to fetch playback URL for existing live stream:', e?.message || e);
                }
                return true;
            }
        } catch (err) {
            if (DEBUG_STREAM) console.log('No existing stream found:', err.message);
        }
        return false;
    }, [streamingService, streamSessionId]);

    // Start stream
    const startStream = useCallback(async (type = 'match', forceRestart = false) => {
        try {
            setError(null);
            setIsLoading(true);
            setStreamingState(STREAMING_STATES.STARTING);
            setStreamType(type || 'match');
            explicitlyStoppedRef.current = false; // Reset explicit stop flag when starting
            
            // Only clear state if forcing a restart, otherwise preserve existing playback URL
            if (forceRestart) {
                setPlaybackUrl('');
                setStreamStatus(null);
                setStreamSessionId('');
                if (DEBUG_STREAM) console.log('Force restart - clearing stream state');
            }

            // Check if stream already exists first (unless forcing restart)
            if (!forceRestart) {
                const hasExistingStream = await checkExistingStream(type);
                if (hasExistingStream) {
                    setIsLoading(false);
                    if (DEBUG_STREAM) console.log('Connected to existing stream of same type');
                    // Try to include the session id in the response for callers that need it
                    let sid = '';
                    try {
                        const st = await streamingService.getStatus(type);
                        sid = st?.stream_session_id || '';
                        if (sid) setStreamSessionId(sid);
                    } catch (_) {}
                    return { success: true, message: 'Connected to existing stream', stream_session_id: sid };
                }
            }

            // If we're switching stream types or forcing restart, clear state now
            if (forceRestart || (streamType && streamType !== type)) {
                setPlaybackUrl('');
                setStreamStatus(null);
                setStreamSessionId('');
                if (DEBUG_STREAM) console.log('Switching stream types or force restart - clearing state');
            }

            // Start new stream
            const result = await streamingService.startStream(type, {
                force: true,
                waitForLive: true,
                debug: false
            });

            if (result?.stream_session_id) setStreamSessionId(result.stream_session_id);

            // Wait for stream to go live
            waitAbortRef.current = false;
            await streamingService.waitForLive(30000, 2000, type, waitAbortRef);

            // Get playback URL
            const playbackData = await streamingService.getPlaybackUrl(type);
            {
                let url = playbackData.playback_url;
                const sid = result?.stream_session_id || streamSessionId;
                if (sid) {
                    const sep = url.includes('?') ? '&' : '?';
                    url = `${url}${sep}sid=${encodeURIComponent(sid)}&t=${Date.now()}`;
                }
                // Force teardown/re-init path for robustness when (re)starting streams
                setPlaybackUrl('');
                setTimeout(() => setPlaybackUrl(url), 0);
            }

            setStreamingState(STREAMING_STATES.LIVE);
            setIsLoading(false);

            // Ensure we can return a session id to the caller
            let sid = streamSessionId;
            try {
                // Prefer id from start response, else from latest status
                sid = result?.stream_session_id || sid;
                if (!sid) {
                    const st = await streamingService.getStatus(type);
                    sid = st?.stream_session_id || '';
                }
            } catch (_) {}
            return { success: true, message: 'Stream started successfully', stream_session_id: sid };
        } catch (err) {
            console.error('Stream start failed');
            if (DEBUG_STREAM) console.error(err);
            setError(err.message);
            setStreamingState(STREAMING_STATES.ERROR);
            setIsLoading(false);
            return { success: false, error: err.message };
        }
    }, [streamingService, checkExistingStream, streamType]);

    // Stop stream
    const stopStream = useCallback(async (onStoppingCallback = null) => {
        try {
            setError(null);
            setIsLoading(true);
            setStreamingState(STREAMING_STATES.STOPPING);

            // Signal any ongoing waits to abort before stopping
            waitAbortRef.current = true;
            explicitlyStoppedRef.current = true; // Mark as explicitly stopped
            
            // Call the stopping callback if provided (for UI updates)
            if (onStoppingCallback && typeof onStoppingCallback === 'function') {
                onStoppingCallback();
            }
            
            const result = await streamingService.stopStream(streamType || 'match', streamSessionId || undefined);
            
            // Immediately set to idle state after successful stop
            setStreamingState(STREAMING_STATES.IDLE);
            setPlaybackUrl('');
            setStreamStatus(null);
            setStreamSessionId('');
            setIsLoading(false);

            if (DEBUG_STREAM) console.log('Stream stopped successfully:', result);
            return { success: true, message: 'Stream stopped successfully' };
        } catch (err) {
            console.error('Stream stop failed');
            if (DEBUG_STREAM) console.error(err);
            setError(err.message);
            setStreamingState(STREAMING_STATES.ERROR);
            setIsLoading(false);
            return { success: false, error: err.message };
        }
    }, [streamingService, streamType, streamSessionId]);

    // Wait until IVS is live then connect (fetch playback URL)
    const waitAndConnect = useCallback(async (timeout = 60000, pollInterval = 1500, typeOverride) => {
        try {
            setError(null);
            setIsLoading(true);
            setStreamingState(STREAMING_STATES.STARTING);
            const t = typeOverride || streamType || 'match';
            if (typeOverride) setStreamType(typeOverride);

            // Wait for live
            waitAbortRef.current = false;
            const status = await streamingService.waitForLive(timeout, pollInterval, t, waitAbortRef);
            if (status?.stream_session_id) setStreamSessionId(status.stream_session_id);

            // Fetch playback URL
            const playbackData = await streamingService.getPlaybackUrl(t);
            {
                let url = playbackData.playback_url;
                const sid = status?.stream_session_id || streamSessionId;
                if (sid) {
                    const sep = url.includes('?') ? '&' : '?';
                    url = `${url}${sep}sid=${encodeURIComponent(sid)}&t=${Date.now()}`;
                }
                // Force teardown/re-init path for robustness when connecting to a live stream
                setPlaybackUrl('');
                setTimeout(() => setPlaybackUrl(url), 0);
            }

            setStreamingState(STREAMING_STATES.LIVE);
            setIsLoading(false);
            return { success: true, stream_session_id: status?.stream_session_id || streamSessionId || '' };
        } catch (err) {
            if (String(err?.message || '').toLowerCase() === 'aborted') {
                setIsLoading(false);
                setStreamingState(STREAMING_STATES.IDLE);
                return { success: false, aborted: true };
            }
            setError(err.message);
            setStreamingState(STREAMING_STATES.ERROR);
            setIsLoading(false);
            return { success: false, error: err.message };
        }
    }, [streamingService, streamType]);

    // Check for existing streams on mount (auto-connect if found)
    useEffect(() => {
        checkExistingStream(defaultType);
    }, [checkExistingStream, defaultType]);

    const value = {
        // State
        streamingState,
        isLoading,
        playbackUrl,
        error,
        streamStatus,
        streamSessionId,
        streamType,

        // Actions
        startStream,
        stopStream,
        waitAndConnect,
        checkExistingStream,
        clearError,
        clearStreamState,
        setStreamType,
        setPlaybackUrl,

        // Service
        streamingService
    };

    return (
        <StreamingContext.Provider value={value}>
            {children}
        </StreamingContext.Provider>
    );
}

// Hook to use streaming context
export function useStreaming() {
    const context = useContext(StreamingContext);
    if (!context) {
        throw new Error('useStreaming must be used within a StreamingProvider');
    }
    return context;
}
